import { useNavigate } from "react-router-dom";
import { Button, CoupleSessionRow, EmptyState, FriendlyError, SectionLoading } from "@codel/dova-ds";
import { useMeContext } from "../MeApp";
import { usePastSessions, type PastSession } from "../hooks/sessionHooks";
import { FALLBACK_THERAPIST_NAME, PAST_SESSIONS_COPY } from "./pastSessionsCopy";
import { BackButton } from "../components/BackButton";

// Copy lives in `pastSessionsCopy.ts`; the row dates below are FORMATTING.

function formatDate(session: PastSession): string {
  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: session.tzid,
  }).format(new Date(session.startLocal));
}

function formatTime(session: PastSession): string {
  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: session.tzid,
  }).format(new Date(session.startLocal));
}

export function PastSessionsPage() {
  const navigate = useNavigate();
  const { couple } = useMeContext();
  const { sessions, hasMore, loadMore, isLoading, isLoadingMore, error } = usePastSessions();
  const therapistName = couple?.therapistName ?? FALLBACK_THERAPIST_NAME;

  return (
    <main className="flex min-h-dvh flex-col gap-6 px-6 py-8">
      <header className="flex flex-col gap-4">
        <BackButton label={PAST_SESSIONS_COPY.backLabel} onClick={() => navigate("/me/home")} />
        <div>
          <h1 className="m-0 font-serif text-2xl font-medium leading-tight text-primary">{PAST_SESSIONS_COPY.title}</h1>
          <p className="mt-2 [font:var(--type-body)] text-secondary">{PAST_SESSIONS_COPY.sub(therapistName)}</p>
        </div>
      </header>

      {isLoading ? <SectionLoading label={PAST_SESSIONS_COPY.loadingLabel} /> : null}
      <FriendlyError detail={error?.message ?? null} />

      {!isLoading && !error && sessions.length === 0 ? (
        <EmptyState title={PAST_SESSIONS_COPY.emptyTitle} body={PAST_SESSIONS_COPY.emptyBody} />
      ) : null}

      {sessions.length > 0 ? (
        <section className="overflow-hidden rounded-[var(--radius-lg)] border border-border bg-surface px-4">
          {sessions.map((session) => (
            <CoupleSessionRow
              key={session.uuid}
              number={session.sessionNumber}
              date={formatDate(session)}
              time={formatTime(session)}
              action={
                !session.hasFeedback && session.feedbackWindowOpen
                  ? {
                      label: PAST_SESSIONS_COPY.feedbackLabel,
                      ariaLabel: PAST_SESSIONS_COPY.feedbackAriaLabel(session.sessionNumber),
                      onClick: () => navigate(`/me/feedback/${session.uuid}`),
                    }
                  : undefined
              }
            />
          ))}
        </section>
      ) : null}

      {hasMore ? (
        <Button variant="outline" size="xl" onClick={loadMore} disabled={isLoadingMore}>
          {isLoadingMore ? PAST_SESSIONS_COPY.loadingMoreLabel : PAST_SESSIONS_COPY.loadMoreLabel}
        </Button>
      ) : null}
    </main>
  );
}
